import { z } from 'zod';

export const ContentFreePostHogDimensionsSchema = z.object({
  $browser: z
    .enum([
      'Chrome',
      'Chrome iOS',
      'Firefox',
      'Firefox iOS',
      'Safari',
      'Mobile Safari',
      'Microsoft Edge',
      'Opera',
      'Opera Mini',
      'Samsung Internet',
      'UC Browser',
      'Internet Explorer',
      'Internet Explorer Mobile',
      'Android Mobile',
      'Facebook Mobile',
      'BlackBerry',
      'Konqueror',
    ])
    .optional()
    .catch(undefined),
  $browser_version: z.number().nonnegative().optional().catch(undefined),
  $os: z
    .enum([
      'Windows',
      'Windows Phone',
      'Windows Mobile',
      'Mac OS X',
      'iOS',
      'Android',
      'Linux',
      'Chrome OS',
      'watchOS',
      'Xbox',
      'Nintendo',
      'PlayStation',
      'BlackBerry',
    ])
    .optional()
    .catch(undefined),
  $os_version: z
    .string()
    .regex(/^(?:\d+(?:\.\d+){0,3}|NT 3\.11|NT 4\.0|XP|Vista|RT)$/)
    .optional()
    .catch(undefined),
  $device_type: z
    .enum(['Desktop', 'Mobile', 'Tablet', 'Console', 'Wearable'])
    .optional()
    .catch(undefined),
  $current_url: z
    .string()
    .refine((value) => {
      try {
        return new URL(value).origin === window.location.origin;
      } catch {
        return false;
      }
    })
    .optional()
    .catch(undefined),
  $pathname: z.string().optional().catch(undefined),
});

const ContentFreeStaffHostSchema = z
  .string()
  .refine((value) => value === window.location.host)
  .optional()
  .catch(undefined);
/** External hostnames can contain sensitive labels, so referrers retain the same origin boundary as URLs. */
const ContentFreeReferringDomainSchema = z
  .union([z.literal('$direct'), ContentFreeStaffHostSchema])
  .optional()
  .catch(undefined);

export const ContentFreePageviewDimensionsSchema = ContentFreePostHogDimensionsSchema.extend({
  $referrer: z
    .union([z.literal('$direct'), ContentFreePostHogDimensionsSchema.shape.$current_url])
    .optional()
    .catch(undefined),
  $host: ContentFreeStaffHostSchema,
  $referring_domain: ContentFreeReferringDomainSchema,
  $pageview_id: z.uuid().optional().catch(undefined),
  $lib: z.literal('web').optional().catch(undefined),
  $lib_version: z
    .string()
    .regex(/^\d+\.\d+\.\d+(?:-(?:alpha|beta|rc)\.\d+)?$/)
    .optional()
    .catch(undefined),
  $timezone: z
    .string()
    .refine((value) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
      } catch {
        return false;
      }
    })
    .optional()
    .catch(undefined),
  $timezone_offset: z.number().int().min(-840).max(840).optional().catch(undefined),
  $browser_language: z
    .string()
    .regex(/^[a-z]{2,3}(?:-[A-Z][a-z]{3})?(?:-(?:[A-Z]{2}|\d{3}))?$/)
    .pipe(z.string().refine((value) => Intl.DateTimeFormat.supportedLocalesOf([value]).length > 0))
    .optional()
    .catch(undefined),
  $browser_language_prefix: z
    .string()
    .regex(/^[a-z]{2,3}$/)
    .pipe(z.string().refine((value) => Intl.DateTimeFormat.supportedLocalesOf([value]).length > 0))
    .optional()
    .catch(undefined),
  $screen_height: z.number().nonnegative().optional().catch(undefined),
  $screen_width: z.number().nonnegative().optional().catch(undefined),
  $viewport_height: z.number().nonnegative().optional().catch(undefined),
  $viewport_width: z.number().nonnegative().optional().catch(undefined),
  $prev_pageview_id: z.uuid().optional().catch(undefined),
  $prev_pageview_duration: z.number().nonnegative().optional().catch(undefined),
  $prev_pageview_pathname: ContentFreePostHogDimensionsSchema.shape.$pathname,
  $session_entry_host: ContentFreeStaffHostSchema,
  $session_entry_referring_domain: ContentFreeReferringDomainSchema,
  $session_entry_pathname: ContentFreePostHogDimensionsSchema.shape.$pathname,
  $session_entry_url: ContentFreePostHogDimensionsSchema.shape.$current_url,
  $session_entry_referrer: z
    .union([z.literal('$direct'), ContentFreePostHogDimensionsSchema.shape.$current_url])
    .optional()
    .catch(undefined),
});
